import { AlertTriangle, Church, Mail, Users } from 'lucide-react'
import { toast } from 'react-toastify'
import { useSendBulkAnnualReportsMutation } from '../../receipts/hooks'
import { Button } from '@/components/ui/button'
import { Dialog } from '@/components/custom/dialog'
import LoadingButton from '@/components/custom/loading-button'

interface ChurchStats {
  church: {
    _id: string
    name: string
    address: {
      city: string
      province: string
    }
  }
  donorCount: number
  totalDonations: number
  totalAmount: number
}

interface ConfirmSendBulkEmailsProps {
  trigger?: React.ReactNode
  open?: boolean
  onClose?: () => void
  year: number
  churchId?: string
  churchStats: Array<ChurchStats>
  onSuccess?: () => void
}

export function ConfirmSendBulkEmails({
  trigger,
  open,
  onClose,
  year,
  churchId,
  churchStats,
  onSuccess,
}: ConfirmSendBulkEmailsProps) {
  const { mutateAsync: sendBulkReports, isPending: isSendingReports } =
    useSendBulkAnnualReportsMutation()

  // Calculate stats for the target churches
  const targetChurches = churchId
    ? churchStats.filter((stat) => stat.church._id === churchId)
    : churchStats

  const totalEmails = targetChurches.reduce(
    (sum, stat) => sum + stat.donorCount,
    0,
  )

  const targetChurch = churchId ? targetChurches[0]?.church : null

  const handleSendReports = async () => {
    try {
      if (targetChurches.length === 0) {
        toast.error('No donors found for the selected criteria')
        return
      }

      const response = await sendBulkReports({
        year,
        churchId: churchId || undefined,
      })

      if (response.errors && response.errors.length > 0) {
        toast.success(
          `Sent ${response.emailsSent} of ${response.totalDonors} reports. ${response.errors.length} had issues.`,
        )
        console.warn('Some emails had issues:', response.errors)
      } else {
        toast.success(
          `Successfully sent all ${response.emailsSent} annual donation summary emails!`,
        )
      }

      onSuccess?.()
      onClose?.()
    } catch (error) {
      console.error('Error sending bulk reports:', error)
      toast.error('Failed to send reports. Please try again.')
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      trigger={trigger}
      title="Confirm Send Bulk Emails"
      contentClassName="max-w-md"
    >
      <div className="space-y-4">
        {/* Warning Icon */}
        <div className="flex items-center justify-center">
          <div className="flex items-center justify-center w-12 h-12 bg-amber-100 rounded-full">
            <AlertTriangle className="h-6 w-6 text-amber-600" />
          </div>
        </div>

        {/* Confirmation Message */}
        <div className="text-center">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Send Annual Donation Summaries
          </h3>
          <p className="text-gray-600">
            This will send annual donation summary emails for{' '}
            <strong>{year}</strong> to:
          </p>
        </div>

        {/* Summary Stats */}
        <div className="bg-gray-50 rounded-lg p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-gray-500" />
              <span className="text-sm font-medium">Total Emails</span>
            </div>
            <span className="text-sm font-bold text-red-600">
              {totalEmails}
            </span>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Church className="h-4 w-4 text-gray-500" />
              <span className="text-sm font-medium">
                {targetChurch ? 'Church' : 'Churches'}
              </span>
            </div>
            <span className="text-sm">
              {targetChurch ? (
                <div className="text-right">
                  <div className="font-medium">{targetChurch.name}</div>
                  <div className="text-xs text-gray-500">
                    {targetChurch.address.city}, {targetChurch.address.province}
                  </div>
                </div>
              ) : (
                `${targetChurches.length} churches`
              )}
            </span>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-gray-500" />
              <span className="text-sm font-medium">Total Donors</span>
            </div>
            <span className="text-sm">{totalEmails}</span>
          </div>
        </div>

        {/* Warning Message */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
            <div className="text-sm text-amber-800">
              <p className="font-medium">Important</p>
              <p>
                This action will send emails to all selected donors. Please
                ensure this is correct before proceeding.
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 pt-2">
          <Button
            variant="outline"
            onClick={onClose}
            className="flex-1"
            disabled={isSendingReports}
          >
            Cancel
          </Button>
          <LoadingButton
            onClick={handleSendReports}
            className="flex-1"
            isLoading={isSendingReports}
            disabled={totalEmails === 0}
          >
            <Mail className="h-4 w-4 mr-2" />
            {isSendingReports ? 'Sending...' : `Send ${totalEmails} Emails`}
          </LoadingButton>
        </div>
      </div>
    </Dialog>
  )
}

export default ConfirmSendBulkEmails
